import type { RefObject } from "react";
import { useCallback, useEffect, useState } from "react";
import { useMatchMedia } from "~/hooks/use-match-media";
import { useResizeWindow } from "~/hooks/use-resize-window";

const PREVIEW_MODAL_HEIGHT = 320;

type UseCarouselArgs = {
  resizeWatchRef: RefObject<HTMLDivElement>;
  displayItemCount: number;
  uniqueId?: string;
  isRankingType?: boolean;
};

export const useCarousel = ({ resizeWatchRef, displayItemCount, uniqueId }: UseCarouselArgs) => {
  const [isShowPrevButton, setIsShowPrevButton] = useState(false);
  const [isShowNextButton, setIsShowNextButton] = useState(true);

  const [scrollAmount, setScrollAmount] = useState(0);

  const [margin, setMargin] = useState(0);

  const { isSpSize } = useMatchMedia();

  const { windowWidth } = useResizeWindow({ shouldIncludedScrollBarWidth: true, debounceTime: 1000 });

  const onPrevButtonClicked = useCallback(() => {
    if (!resizeWatchRef.current) return;
    const scope = resizeWatchRef.current;
    const { scrollLeft } = scope;
    if (scrollLeft === 0) return;

    scope.scrollTo({ left: scrollLeft - scrollAmount, behavior: "smooth" });
  }, [resizeWatchRef, scrollAmount, displayItemCount]);

  const onNextButtonClicked = useCallback(() => {
    if (!resizeWatchRef.current) return;
    const scope = resizeWatchRef.current;
    const { scrollLeft, scrollWidth, clientWidth } = scope;

    if (scrollWidth - Math.ceil(scrollLeft) === clientWidth) return;

    scope.scrollTo({ left: scrollLeft + scrollAmount, behavior: "smooth" });
  }, [resizeWatchRef, scrollAmount, displayItemCount]);

  const handleHorizontalScroll = () => {
    if (!resizeWatchRef.current) return;
    const scope = resizeWatchRef.current;

    const { scrollLeft, scrollWidth, clientWidth } = scope;

    setIsShowPrevButton(scrollLeft !== 0);

    const isDisplayFinalElement =
      scrollWidth - Math.ceil(scrollLeft) - clientWidth / displayItemCount / 2 < clientWidth;

    setIsShowNextButton(!isDisplayFinalElement);
  };

  // memo: displayItemCountの計算完了後を検知してからイベントをlistenしたいため、displayItemCount === 0 の時、再帰的に処理を返す
  useEffect(() => {
    if (!resizeWatchRef.current || displayItemCount === 0) return;
    const scope = resizeWatchRef.current;
    scope.addEventListener("scroll", handleHorizontalScroll, { passive: true });
    return () => {
      scope.removeEventListener("scroll", handleHorizontalScroll);
    };
  }, [displayItemCount]);

  // memo: 画面リサイズが発生した時にネガティブmarginを再計算する処理
  // useResizeWindowでdebounce1,000msごとに発火させることでperformance調整
  useEffect(() => {
    const lazyImageWrappers = Array.from(document.getElementsByClassName(`lazy-image-wrapper-${uniqueId}`));
    const margin = (PREVIEW_MODAL_HEIGHT - (lazyImageWrappers[0]?.clientHeight || 0)) / 2;
    setMargin(margin > 0 ? margin : 0);
  }, [windowWidth]);

  useEffect(() => {
    const scope = resizeWatchRef.current;
    if (scope === null) return;
    setScrollAmount((scope.clientWidth || windowWidth) * 0.7);
  }, [displayItemCount, isSpSize]);

  return {
    isShowPrevButton,
    isShowNextButton,
    margin,
    onPrevButtonClicked,
    onNextButtonClicked,
  };
};
